import {
	IconAlertCircle,
	IconInfoCircle,
	IconTrendingUp,
} from "@tabler/icons-react";
import type { CSSProperties } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
	type BillingUsageItem,
	formatFeatureName,
	getFeatureIcon,
	getKeyFeatures,
	getUsageStatus,
	keyMetricsAttentionLabel,
	usageLimit,
	utilizationBadgeVariant,
} from "@/lib/billing-usage";
import { cn } from "@/lib/utils";

type UsageStatusLabelProps = {
	status: ReturnType<typeof getUsageStatus>["status"];
	className?: string;
};

function UsageStatusLabel({ status, className }: UsageStatusLabelProps) {
	return (
		<div
			className={cn(
				"flex items-center gap-1.5 text-sm/6 sm:text-xs/5",
				className,
			)}
		>
			{status === "critical" && (
				<>
					<IconAlertCircle className="size-4 shrink-0 text-destructive sm:size-3.5" />
					<span className="font-medium text-destructive">Critical</span>
				</>
			)}
			{status === "warning" && (
				<>
					<IconInfoCircle className="text-warning size-4 shrink-0 sm:size-3.5" />
					<span className="text-warning font-medium">Warning</span>
				</>
			)}
			{status === "normal" && (
				<span className="text-muted-foreground">Normal</span>
			)}
		</div>
	);
}

type KeyMetricCardProps = {
	item: BillingUsageItem;
	className?: string;
};

function KeyMetricCard({ item, className }: KeyMetricCardProps) {
	const limit = usageLimit(item.limit);
	const Icon = getFeatureIcon(item.feature_key);
	const { status, color } = getUsageStatus(item.usage, limit);
	const percentage = limit ? (item.usage / limit) * 100 : 0;

	return (
		<Card
			className={cn(
				"group relative overflow-hidden border border-border bg-linear-to-br from-card to-muted/15 shadow-md transition-all duration-300 hover:border-primary/25 hover:shadow-lg",
				className,
			)}
		>
			<div
				className="absolute inset-x-0 top-0 h-1.5"
				style={{ backgroundColor: color }}
			/>
			<div className="absolute inset-0 bg-linear-to-br from-primary/5 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

			<CardHeader className="relative flex flex-row items-start justify-between space-y-0 pb-4">
				<div className="min-w-0 space-y-1.5">
					<CardTitle className="max-w-[24ch] text-balance text-xl/7 font-semibold sm:text-lg/7">
						{formatFeatureName(item.feature_key)}
					</CardTitle>
					<UsageStatusLabel status={status} />
				</div>
				<div className="shrink-0 rounded-xl border border-primary/20 bg-linear-to-br from-primary/20 to-primary/10 p-3 transition-all duration-300 group-hover:from-primary/30 group-hover:to-primary/20">
					<Icon className="size-6 shrink-0 text-primary sm:size-5" />
				</div>
			</CardHeader>

			<CardContent className="relative">
				<div className="space-y-4">
					<div className="flex flex-wrap items-baseline justify-between gap-2">
						<span className="text-4xl/10 font-bold tracking-tight sm:text-3xl/9">
							{item.usage.toLocaleString()}
						</span>
						{limit && (
							<span className="text-base/7 font-medium text-muted-foreground sm:text-sm/6">
								/ {limit.toLocaleString()}
							</span>
						)}
					</div>

					{limit ? (
						<>
							<Progress
								value={percentage}
								className="h-3 bg-muted/50"
								style={
									{
										"--progress-background": color,
									} as CSSProperties
								}
							/>
							<div className="flex flex-wrap items-center justify-between gap-2 text-base/7 sm:text-sm/6">
								<span className="font-medium text-muted-foreground">
									{percentage.toFixed(1)}% utilized
								</span>
								<Badge
									variant={utilizationBadgeVariant(status)}
									className="font-medium"
								>
									{limit - item.usage > 0
										? `${(limit - item.usage).toLocaleString()} left`
										: "At limit"}
								</Badge>
							</div>
						</>
					) : (
						<Badge
							variant="outline"
							className="w-full justify-center py-2 font-medium"
						>
							Unlimited
						</Badge>
					)}
				</div>
			</CardContent>
		</Card>
	);
}

type KeyMetricsOverviewProps = {
	data: BillingUsageItem[];
	className?: string;
};

export function KeyMetricsOverview({
	data,
	className,
}: KeyMetricsOverviewProps) {
	const keyFeatures = getKeyFeatures(data);

	return (
		<div className={className}>
			<div className="mb-6 flex items-center justify-between">
				<div className="min-w-0">
					<h2 className="max-w-[24ch] text-balance text-2xl/8 font-bold tracking-tight sm:text-xl/8">
						Key Metrics
					</h2>
					<p className="mt-1 max-w-[40ch] text-pretty text-base/7 text-muted-foreground sm:text-sm/6">
						{keyMetricsAttentionLabel(keyFeatures.length)}
					</p>
				</div>
			</div>
			{keyFeatures.length > 0 ? (
				<div className="@container">
					<div className="grid grid-cols-1 gap-6 @md:grid-cols-2 @xl:grid-cols-3">
						{keyFeatures.map((item) => (
							<KeyMetricCard key={item.feature_key} item={item} />
						))}
					</div>
				</div>
			) : (
				<Card className="border border-border bg-card shadow-sm">
					<CardContent className="py-12 text-center">
						<IconTrendingUp className="mx-auto mb-4 size-12 text-muted-foreground opacity-50" />
						<p className="font-medium text-muted-foreground">
							No key metrics to display
						</p>
						<p className="mt-1 text-base/7 text-muted-foreground sm:text-sm/6">
							Usage data will appear here as features are used
						</p>
					</CardContent>
				</Card>
			)}
		</div>
	);
}
